/**
 * Freelancer teaching side: batch offers, my batches, a batch workspace (classes, students, assignments,
 * materials) and grading. Money is only ever shown, never calculated here.
 */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html, safeUrl } from '../../core/html.js';
import { fmtDateTime, fmtWhen, fromIstInput, inr, toIstInput, weekdayNames } from '../../core/format.js';
import { reload, update } from '../../app/shell.js';
import { backLink, banner, empty, ic, seg, stat, statusTag, table, tag, who, bar } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { filePicker, uploadChosen } from '../../ui/upload.js';
import { classState } from '../classes/list.js';
import { testsTab } from '../tests/trainer.js';

const quiet = (p, fallback) => p.catch(() => fallback);

/* ---------- coaching home: offers + batches ---------- */

const coaching = {
  path: '/professional/coaching',
  roles: ['professional'],
  title: 'Coaching',
  async load(ctx) {
    const [offers, batches, upcoming] = await Promise.all([
      api.get('/trainer/offers', { signal: ctx.signal }),
      api.get('/trainer/batches', { signal: ctx.signal }),
      quiet(api.get('/classes', { signal: ctx.signal, query: { scope: 'upcoming' } }), { classes: [] }),
    ]);
    return { offers: offers.offers, batches: batches.batches, upcoming: upcoming.classes };
  },
  view(d) {
    return html`${d.offers.map((o) => html`<div class="card mb">
        <div class="row between"><div><span class="tag wn">Batch offer</span><h3 style="margin:6px 0 2px">${o.course.title}, ${o.name}</h3>
          <p class="mu sm" style="margin:0">${weekdayNames(o.weekdays)} at ${o.start_time}. Starts ${o.starts_on ? fmtWhen(o.starts_on) : 'soon'}. Up to ${o.capacity} students.</p></div>
          <div class="stat" style="text-align:right"><div class="k">${inr(o.pay_per_class_paise)}</div><div class="l">per class taught</div></div></div>
        <div class="row mt-s"><button type="button" class="btn" data-act="offer.respond" data-id="${o.id}" data-accept="true" data-name="${o.name}">Accept</button>
          <button type="button" class="btn ghost" data-act="offer.respond" data-id="${o.id}" data-accept="false" data-name="${o.name}">Decline</button></div></div>`)}
      <div class="page-h"><div><h2 style="margin:0 0 4px">Your batches</h2><p>Batches you teach. Open one to run classes, set work and grade it.</p></div></div>
      ${d.batches.length
        ? html`<div class="grid g2">${d.batches.map((b) => html`<div class="card"><div class="row between"><h3 style="margin:0">${b.course.title}</h3>${statusTag(b.status)}</div>
          <p class="mu sm">${b.name}. ${b.schedule || weekdayNames(b.weekdays)}</p>
          <div class="row sm"><span>${ic('users')} ${b.students} students</span><span>${ic('check')} ${b.classes_done} classes taken</span></div>
          <p class="sm mt-s">${b.next_class ? html`Next: class ${b.next_class.number}, <b>${fmtWhen(b.next_class.starts_at)}</b>` : 'No class scheduled'}</p>
          <div class="row">${b.next_class ? html`<a class="btn sm" href="/professional/classes/${b.next_class.id}">${ic('play')} Open class</a>` : ''}<a class="btn ghost sm" href="/professional/batches/${b.id}">Manage batch</a></div></div>`)}</div>`
        : empty('play', 'No batches yet', 'When Vignova offers you a batch it appears here for you to accept.')}
      ${d.upcoming.length ? html`<h3 class="mt">Upcoming classes</h3>${table(['Course', 'Class', 'When', 'Status', ''], d.upcoming.slice(0, 8).map((c) => [c.course, `#${String(c.number).padStart(2, '0')} ${c.title}`, fmtWhen(c.starts_at), classState(c), html`<a class="btn ghost sm" href="/professional/classes/${c.id}">Open</a>`]))}` : ''}`;
  },
};

register({
  'offer.respond': async (el) => {
    const accept = el.dataset.accept === 'true';
    if (!accept && !(await confirmBox({ title: `Decline ${el.dataset.name}?`, text: 'Vignova will offer this batch to someone else.', label: 'Decline', danger: true }))) return;
    await api.post(`/trainer/offers/${el.dataset.id}/respond`, { body: { accept } });
    toast(accept ? 'Batch accepted. Students will see you as their trainer.' : 'Offer declined', accept ? 'check' : 'out');
    await reload();
  },
});

/* ---------- batch workspace ---------- */

const batchPage = {
  path: '/professional/batches/:id',
  roles: ['professional'],
  nav: 'coaching',
  title: (d) => (d ? `${d.batch.course.title}, ${d.batch.name}` : 'Batch'),
  ui: (ctx) => ({ tab: ['classes', 'students', 'assignments', 'tests', 'materials'].includes(ctx.query.get('tab')) ? ctx.query.get('tab') : 'classes', added: [] }),
  async load(ctx) {
    const id = Number(ctx.params.id);
    const { batches } = await api.get('/trainer/batches', { signal: ctx.signal });
    const batch = batches.find((b) => b.id === id);
    if (!batch) throw Object.assign(new Error('This batch is not one of yours.'), { status: 404 });
    const [classes, students, assignments, tests] = await Promise.all([
      api.get('/classes', { signal: ctx.signal, query: { scope: 'all', batch_id: id } }),
      api.get(`/trainer/batches/${id}/students`, { signal: ctx.signal }),
      api.get('/trainer/assignments', { signal: ctx.signal, query: { batch_id: id } }),
      api.get('/trainer/tests', { signal: ctx.signal, query: { batch_id: id } }),
    ]);
    return { batch, classes: classes.classes, students: students.students, assignments: assignments.assignments, tests: tests.tests };
  },
  view(d, ui) {
    const b = d.batch;
    const tabs = [['classes', `Classes (${d.classes.length})`], ['students', `Students (${d.students.length})`], ['assignments', `Assignments (${d.assignments.length})`], ['tests', `Tests (${d.tests.length})`], ['materials', 'Materials']];
    return html`${backLink('/professional/coaching', 'Coaching')}
      <div class="page-h"><div><h2 style="margin:0 0 4px">${b.course.title}, ${b.name}</h2><p>${b.schedule || weekdayNames(b.weekdays)}. ${inr(b.pay_per_class_paise)} per class.</p>${b.note ? html`<p class="note" style="white-space:pre-wrap">${b.note}</p>` : ''}</div>${statusTag(b.status)}</div>
      <div class="grid stats">${stat(d.students.length, 'Students')}${stat(b.classes_done, 'Classes taken')}${stat(d.assignments.length, 'Assignments')}${stat(b.seats_left ?? '-', 'Seats left')}</div>
      <div class="mt mb">${seg(tabs, ui.tab, 'batch.tab', 'Batch sections')}</div>
      ${ui.tab === 'classes' ? classesTab(d) : ui.tab === 'students' ? studentsTab(d) : ui.tab === 'assignments' ? assignmentsTab(d) : ui.tab === 'tests' ? testsTab(d) : materialsTab(d, ui)}`;
  },
};

function classesTab(d) {
  if (!d.classes.length) return empty('cal', 'No classes yet', 'Vignova schedules classes for the batch. They appear here.');
  return table(['Class', 'When', 'Length', 'Status', ''], d.classes.map((c) => [
    `#${String(c.number).padStart(2, '0')} ${c.title}`, fmtDateTime(c.starts_at), `${c.duration_min} min`, classState(c),
    c.status === 'scheduled' || c.status === 'live'
      ? html`<div class="row nw"><a class="btn sm" href="/professional/classes/${c.id}">${c.status === 'live' ? 'Rejoin' : 'Open'}</a>
        ${c.status === 'scheduled' ? html`<button type="button" class="btn ghost sm" data-act="class.reschedule" data-id="${c.id}" data-at="${c.starts_at}" data-n="${c.number}">Reschedule</button>
        <button type="button" class="btn danger sm" data-act="class.cancel" data-id="${c.id}" data-n="${c.number}">Cancel</button>` : ''}</div>`
      : html`<a class="btn ghost sm" href="/professional/classes/${c.id}">Details</a>`,
  ]));
}

function studentsTab(d) {
  if (!d.students.length) return empty('users', 'No students yet', 'Students appear here once they join this batch.');
  return table(['Student', 'Course progress', 'Attendance', ''], d.students.map((s) => [
    who(s.name),
    html`<div style="min-width:120px">${bar(s.progress, '', `${s.name} progress`)}<span class="xs mu">${s.progress}%</span></div>`,
    s.attendance_percent == null ? tag('No classes yet', 'n') : tag(`${s.attendance_percent}%`, s.attendance_percent >= 75 ? 'ok' : 'wn'),
    html`<button type="button" class="btn ghost sm" data-act="student.feedback" data-id="${s.user_id}" data-name="${s.name}" data-course="${d.batch.course_id}">Leave feedback</button>`,
  ]));
}

function assignmentsTab(d) {
  return html`<div class="row between mb"><p class="mu sm" style="margin:0">Students submit work from their dashboard. You grade it here.</p><button type="button" class="btn" data-act="assignment.new" data-batch="${d.batch.id}">${ic('plus')} New assignment</button></div>
    ${d.assignments.length
      ? table(['Assignment', 'Due', 'Submitted', 'Graded', ''], d.assignments.map((a) => [a.title, fmtDateTime(a.due_at), `${a.submissions} of ${d.students.length}`, `${a.graded} of ${a.submissions}`, html`<a class="btn ghost sm" href="/professional/assignments/${a.id}">${a.submissions > a.graded ? 'Grade' : 'View'}</a>`]))
      : empty('clip', 'No assignments yet', 'Set work to keep students practising.')}`;
}

function materialsTab(d, ui) {
  return html`<div class="card" style="max-width:600px"><h3>Share a material</h3>
    <p class="mu sm">Slides, notes or code. Students in this batch are notified.</p>
    <form data-submit="material.add" novalidate>${field('mt-title', 'Title', { name: 'title', ph: 'Week 1 slides', minlen: 2, maxlength: 160 })}
      ${filePicker('mt-file', 'File', 'material', { name: 'file', req: true })}
      <input type="hidden" name="batch_id" value="${d.batch.id}"><button class="btn" type="submit">${ic('plus')} Share</button></form></div>
    ${ui.added.length ? html`<h3 class="mt">Shared in this session</h3><div class="list sm">${ui.added.map((m) => html`<div class="li">${ic('file')}<span class="f1">${m.title}</span><button type="button" class="btn ghost sm" data-act="file.open" data-id="${m.file.id}">Open</button></div>`)}</div>` : ''}
    <p class="note mt-s">Shared materials show up on each class page for you and your students.</p>`;
}

register({
  'batch.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),

  'class.reschedule': (el) => openModal({
    title: `Reschedule class ${el.dataset.n}`,
    body: html`<form id="resched-form" data-submit="class.reschedule.send" data-id="${el.dataset.id}" novalidate>${field('rs-at', 'New date and time (India time)', { name: 'starts_at', type: 'datetime-local', val: toIstInput(el.dataset.at) })}</form>
      <p class="note">Students are notified of the new time.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="resched-form" class="btn">Reschedule</button>`,
  }),
  'class.reschedule.send': (form) => submitForm(form, async ({ starts_at: at }) => {
    const iso = fromIstInput(at);
    if (!iso) throw new Error('Choose a valid date and time.');
    if (new Date(iso) < new Date()) throw new Error('Pick a time in the future.');
    await api.post(`/classes/${form.dataset.id}/reschedule`, { body: { starts_at: iso } });
    closeModal(false);
    toast('Class rescheduled', 'cal');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'class.cancel': (el) => openModal({
    title: `Cancel class ${el.dataset.n}?`,
    body: html`<form id="cancel-form" data-submit="class.cancel.send" data-id="${el.dataset.id}" novalidate>${field('cc-reason', 'Reason for students', { name: 'reason', req: false, maxlength: 200, ph: 'Unwell, will reschedule' })}</form>
      <p class="note">Students are told right away. Vignova can schedule a replacement.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Keep class</button><button type="submit" form="cancel-form" class="btn danger">Cancel class</button>`,
  }),
  'class.cancel.send': (form) => submitForm(form, async ({ reason }) => {
    await api.post(`/classes/${form.dataset.id}/cancel`, { body: { reason } });
    closeModal(false);
    toast('Class cancelled', 'out');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'student.feedback': (el) => openModal({
    title: `Feedback for ${el.dataset.name}`,
    body: html`<form id="fb-form" data-submit="student.feedback.send" data-student="${el.dataset.id}" data-course="${el.dataset.course}" novalidate>${field('fb-text', 'Your feedback', { name: 'text', rows: 5, minlen: 3, max: 3000, ph: 'What is going well and what to work on next' })}</form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="fb-form" class="btn">Send feedback</button>`,
  }),
  'student.feedback.send': (form) => submitForm(form, async ({ text }) => {
    await api.post('/trainer/feedback', { body: { student_id: Number(form.dataset.student), course_id: Number(form.dataset.course), text } });
    closeModal(false);
    toast('Feedback sent', 'check');
  }, { onError: (error) => toast(error.message, 'alert') }),

  'assignment.new': (el) => openModal({
    title: 'New assignment',
    wide: true,
    body: html`<form id="asg-form" data-submit="assignment.create" data-batch="${el.dataset.batch}" novalidate>
      ${field('as-title', 'Title', { name: 'title', minlen: 2, maxlength: 200, ph: 'Build a login test suite' })}
      ${field('as-desc', 'What students need to do', { name: 'description', rows: 5, req: false, max: 5000 })}
      ${field('as-link', 'Share a link (optional)', { name: 'link', type: 'url', req: false, maxlength: 500, ph: 'https://docs.google.com/... a brief, a repo or a reading', help: 'Students see this link with the assignment.' })}
      <div class="fgrid">${field('as-due', 'Due (India time)', { name: 'due_at', type: 'datetime-local' })}${field('as-max', 'Maximum score', { name: 'max_score', type: 'number', min: 1, max: 1000, val: 100 })}</div></form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="asg-form" class="btn">Create assignment</button>`,
  }),
  'assignment.create': (form) => submitForm(form, async (v) => {
    const due = fromIstInput(v.due_at);
    if (!due) throw new Error('Choose a due date and time.');
    if (new Date(due) < new Date()) throw new Error('The due date must be in the future.');
    await api.post('/trainer/assignments', { body: { batch_id: Number(form.dataset.batch), title: v.title, description: v.description, due_at: due, max_score: Number(v.max_score) || 100, link: v.link || '' } });
    closeModal(false);
    toast('Assignment created. Students are notified.', 'clip');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'material.add': (form) => submitForm(form, async (v) => {
    const stored = await uploadChosen(form.elements.file, 'material');
    if (!stored) throw new Error('Choose a file to share.');
    const { material } = await api.post('/trainer/materials', { body: { batch_id: Number(v.batch_id), title: v.title, file_id: stored.id } });
    form.reset();
    update((ui) => { ui.added.unshift(material); });
    toast('Material shared with your students', 'check');
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- grading ---------- */

const gradePage = {
  path: '/professional/assignments/:id',
  roles: ['professional'],
  nav: 'coaching',
  title: (d) => (d ? d.assignment.title : 'Assignment'),
  load: (ctx) => api.get(`/trainer/assignments/${Number(ctx.params.id)}/submissions`, { signal: ctx.signal }),
  view(d) {
    const a = d.assignment;
    const pending = d.submissions.filter((s) => s.graded_at == null).length;
    return html`${backLink(`/professional/batches/${a.batch_id}`, 'Batch')}
      <div class="page-h"><div><h2 style="margin:0 0 4px">${a.title}</h2><p>Due ${fmtDateTime(a.due_at)}. Out of ${a.max_score}.</p></div>${pending ? tag(`${pending} to grade`, 'wn') : tag('All graded', 'ok')}</div>
      ${a.description || a.link ? html`<div class="card mb"><p style="margin:0;white-space:pre-line">${a.description}</p>${a.link ? html`<p class="sm"><a href="${safeUrl(a.link)}" target="_blank" rel="noopener noreferrer">${ic('link')} ${a.link}</a></p>` : ''}</div>` : ''}
      ${d.submissions.length
        ? html`<div class="list">${d.submissions.map((s) => html`<div class="card mb" id="sub-${s.id}"><div class="row between">${who(s.student_name, `Submitted ${fmtDateTime(s.submitted_at)}`)}${s.late ? tag('Late', 'wn') : ''}${s.graded_at ? tag(`${s.score} / ${a.max_score}`, 'ok') : tag('Not graded', 'n')}</div>
          ${s.note ? html`<p class="mt-s" style="white-space:pre-line">${s.note}</p>` : ''}
          ${s.file_id ? html`<button type="button" class="btn ghost sm" data-act="file.open" data-id="${s.file_id}">${ic('file')} Open submitted file</button>` : ''}
          ${s.link ? html`<p class="sm"><a href="${safeUrl(s.link)}" target="_blank" rel="noopener noreferrer">${ic('link')} Open shared link</a></p>` : ''}
          <form class="mt-s" data-submit="grade.save" data-id="${s.id}" data-max="${a.max_score}" novalidate><div class="fgrid">${field(`gr-score-${s.id}`, `Score (0 to ${a.max_score})`, { name: 'score', type: 'number', min: 0, max: a.max_score, val: s.score ?? '' })}
            ${field(`gr-fb-${s.id}`, 'Feedback', { name: 'feedback', req: false, maxlength: 3000, val: s.feedback, ph: 'Short note for the student' })}</div>
            <button class="btn sm" type="submit">${s.graded_at ? 'Update grade' : 'Save grade'}</button></form></div>`)}</div>`
        : banner('No submissions yet. Students have until the due date.', '', 'info')}`;
  },
};

register({
  'grade.save': (form) => submitForm(form, async ({ score, feedback }) => {
    const n = Number(score);
    if (!Number.isInteger(n) || n < 0 || n > Number(form.dataset.max)) throw new Error(`Score must be a whole number from 0 to ${form.dataset.max}.`);
    await api.post(`/trainer/submissions/${form.dataset.id}/grade`, { body: { score: n, feedback } });
    toast('Grade saved. The student is notified.', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = [coaching, batchPage, gradePage];
